import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Search, Table2, Key, Link2, Hash, Calendar, Type, FileText, ToggleLeft, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

const semanticBadge = (type) => {
  const map = {
    primary_key: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Primary Key' },
    foreign_key: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Foreign Key' },
    temporal: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Temporal' },
    person_name: { bg: 'bg-sky-100', text: 'text-sky-700', label: 'Person Name' },
    identifier: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Identifier' },
    monetary: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Monetary' },
    code: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Code' },
    categorical: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Categorical' },
    text: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Text' },
    boolean: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Boolean' },
    attribute: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Attribute' },
  };
  const m = map[type] || map.attribute;
  return <span className={`${m.bg} ${m.text} px-2 py-0.5 rounded-full text-[10px] font-medium`}>{m.label}</span>;
};

export default function Glossary() {
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [glossary, setGlossary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTable, setFilterTable] = useState('all');

  const loadSources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/datasources`, authH());
      setSources(data);
      if (data.length > 0 && !selectedSource) {
        setSelectedSource(data[0].id);
      }
    } catch {}
  }, [selectedSource]);

  useEffect(() => { loadSources(); }, [loadSources]);

  useEffect(() => {
    if (!selectedSource) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/semantic/glossary/${selectedSource}`, authH());
        setGlossary(data);
      } catch { setGlossary(null); }
    })();
  }, [selectedSource]);

  const allEntries = useMemo(() => {
    if (!glossary?.tables) return [];
    const entries = [];
    for (const table of glossary.tables) {
      entries.push({ type: 'table', name: table.name, table: table.name, description: table.description, dataType: '-', semanticType: '-', category: table.category, rowCount: table.row_count });
      for (const col of table.columns) {
        entries.push({ type: 'column', name: col.name, table: table.name, description: col.description, dataType: col.type, semanticType: col.semantic_type, isPk: col.is_pk, isFk: col.is_fk });
      }
    }
    return entries;
  }, [glossary]);

  const filtered = useMemo(() => {
    let items = allEntries;
    if (filterTable !== 'all') items = items.filter(e => e.table === filterTable);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(e => e.name.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q) || e.table.toLowerCase().includes(q));
    }
    return items;
  }, [allEntries, filterTable, searchTerm]);

  const tableNames = glossary?.tables?.map(t => t.name) || [];

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 data-testid="glossary-title" className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>Data Glossary</h1>
            <p className="text-[#6b7280] text-sm mt-1">Auto-generated data dictionary for your connected sources</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Select value={selectedSource} onValueChange={setSelectedSource}>
            <SelectTrigger data-testid="glossary-source-select" className="w-52 bg-white border-[#e6e8f2] text-[#1e1e2e] text-sm">
              <SelectValue placeholder="Select source" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#e6e8f2]">
              {sources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterTable} onValueChange={setFilterTable}>
            <SelectTrigger data-testid="glossary-table-filter" className="w-44 bg-white border-[#e6e8f2] text-[#1e1e2e] text-sm">
              <SelectValue placeholder="All tables" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#e6e8f2]">
              <SelectItem value="all">All Tables</SelectItem>
              {tableNames.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-[#e6e8f2] flex-1 max-w-xs">
            <Search size={14} className="text-[#9ca3af]" />
            <input data-testid="glossary-search" type="text" placeholder="Search glossary..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-transparent text-sm text-[#1e1e2e] outline-none w-full placeholder:text-[#9ca3af]" />
          </div>
          <span className="text-xs text-[#9ca3af]">{filtered.length} entries</span>
        </div>

        {/* Table */}
        {!glossary?.tables ? (
          <div className="bg-white rounded-xl border border-[#e6e8f2] p-12 text-center">
            <BookOpen size={48} className="mx-auto text-[#d4d8e6] mb-3" />
            <p className="text-[#6b7280]">{sources.length === 0 ? 'Connect a data source first' : 'Run semantic discovery to generate the glossary'}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e6e8f2] overflow-hidden">
            <div className="scrollbar-light overflow-x-auto">
              <table data-testid="glossary-table" className="w-full">
                <thead>
                  <tr className="bg-[#f5f6fb] border-b border-[#e6e8f2]">
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Name</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Table</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Type</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Semantic Type</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry, i) => (
                    <tr key={`${entry.table}-${entry.name}-${i}`} className={`border-b border-[#f0f1f5] hover:bg-[#f5f6fb]/50 transition-colors ${entry.type === 'table' ? 'bg-[#fafbfd]' : ''}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {entry.type === 'table' ? <Table2 size={13} className="text-[#5b63ea]" /> :
                            entry.isPk ? <Key size={12} className="text-amber-500" /> :
                            entry.isFk ? <Link2 size={12} className="text-blue-500" /> :
                            <span className="w-3" />}
                          <span className={`text-sm ${entry.type === 'table' ? 'font-semibold text-[#1e1e2e]' : 'font-mono text-[#1e1e2e]'}`}>{entry.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-xs text-[#6b7280]">{entry.type === 'table' ? '-' : entry.table}</td>
                      <td className="px-5 py-3 text-xs font-mono text-[#9ca3af]">{entry.dataType}</td>
                      <td className="px-5 py-3">{entry.type === 'column' ? semanticBadge(entry.semanticType) : ''}</td>
                      <td className="px-5 py-3 text-xs text-[#6b7280] max-w-sm truncate">{entry.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
